import React from 'react';
import { Save, X, Loader2, FileText, Upload, Star, StarOff, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const CATEGORIES = [
  'Whitepapers',
  'Guides',
  'Templates',
  'Case Studies',
  'eBooks',
  'Research Reports',
  'Checklists',
  'Toolkits'
];

const ResourceForm = ({
  formData,
  setFormData,
  editingResource,
  activeTab,
  setActiveTab,
  onSubmit,
  onClose,
  isSaving,
  isUploading,
  isUploadingImage,
  fileInputRef,
  imageInputRef,
  onFileUpload,
  onImageUpload,
}) => {
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="resource-form-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">
            {editingResource ? 'Edit Resource' : 'Create New Resource'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6">
          {/* Language Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('en')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              EN English
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('tr')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'tr' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              TR Turkish
            </button>
            <span className="ml-auto text-sm text-gray-500 self-center">
              {activeTab === 'en' ? 'Required fields' : 'Optional translation'}
            </span>
          </div>

          <div className="space-y-6">
            {/* Title */}
            <div>
              <Label htmlFor={`title_${activeTab}`}>
                Title {activeTab === 'en' && <span className="text-red-500">*</span>}
              </Label>
              <Input
                id={`title_${activeTab}`}
                name={`title_${activeTab}`}
                value={formData[`title_${activeTab}`]}
                onChange={handleInputChange}
                placeholder={activeTab === 'en' ? 'Enter resource title' : 'Kaynak basligini girin'}
                required={activeTab === 'en'}
                className="mt-2"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor={`description_${activeTab}`}>
                Description {activeTab === 'en' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id={`description_${activeTab}`}
                name={`description_${activeTab}`}
                value={formData[`description_${activeTab}`]}
                onChange={handleInputChange}
                placeholder={activeTab === 'en' ? 'Brief description of the resource' : 'Kaynagin kisa aciklamasi'}
                required={activeTab === 'en'}
                className="mt-2 min-h-[100px]"
              />
            </div>

            {/* Common Fields (only on English tab) */}
            {activeTab === 'en' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pages">Number of Pages</Label>
                    <Input
                      id="pages"
                      name="pages"
                      type="number"
                      min="1"
                      value={formData.pages}
                      onChange={handleInputChange}
                      placeholder="e.g., 25"
                      className="mt-2"
                    />
                  </div>
                </div>

                {/* PDF Upload */}
                <div>
                  <Label>PDF File <span className="text-red-500">*</span></Label>
                  <div className="mt-2">
                    {formData.fileUrl ? (
                      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <FileText className="w-8 h-8 text-green-600" />
                        <div className="flex-1">
                          <p className="font-medium text-green-800">PDF Uploaded</p>
                          <p className="text-sm text-green-600 truncate max-w-xs">
                            {formData.fileUrl.split('/').pop()}
                          </p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                          Replace
                        </Button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        {isUploading ? (
                          <Loader2 className="w-10 h-10 mx-auto text-blue-600 animate-spin" />
                        ) : (
                          <Upload className="w-10 h-10 mx-auto text-gray-400" />
                        )}
                        <p className="mt-2 text-gray-600">{isUploading ? 'Uploading...' : 'Click to upload PDF'}</p>
                        <p className="text-sm text-gray-400">PDF files only (max 20MB)</p>
                      </div>
                    )}

                    {/* Cover Image Upload */}
                    <div className="mt-4">
                      <Label>Cover Image (Optional)</Label>
                      <div className="mt-2">
                        {formData.image ? (
                          <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <img
                              src={formData.image}
                              alt="Cover preview"
                              className="h-16 w-24 object-cover rounded-lg border"
                              onError={(e) => { e.target.src = 'https://via.placeholder.com/96x64?text=Error'; }}
                            />
                            <div className="flex-1">
                              <p className="font-medium text-blue-800">Cover Image Set</p>
                              <p className="text-sm text-blue-600 truncate max-w-xs">{formData.image.split('/').pop()}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={() => imageInputRef.current?.click()}>Replace</Button>
                              <Button type="button" variant="outline" size="sm" onClick={() => setFormData(prev => ({ ...prev, image: '' }))} className="text-red-600 hover:text-red-700">Remove</Button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => imageInputRef.current?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                          >
                            {isUploadingImage ? (
                              <Loader2 className="w-8 h-8 mx-auto text-blue-600 animate-spin" />
                            ) : (
                              <Image className="w-8 h-8 mx-auto text-gray-400" />
                            )}
                            <p className="mt-2 text-gray-600">{isUploadingImage ? 'Uploading...' : 'Click to upload cover image'}</p>
                            <p className="text-sm text-gray-400">JPG, PNG, GIF, WebP (max 5MB)</p>
                          </div>
                        )}
                        <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={onImageUpload} className="hidden" />
                      </div>
                    </div>

                    <input ref={fileInputRef} type="file" accept=".pdf" onChange={onFileUpload} className="hidden" />
                  </div>
                </div>

                {/* Featured Toggle */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <input type="checkbox" id="featured" name="featured" checked={formData.featured} onChange={handleInputChange} className="w-5 h-5 text-blue-600 rounded" />
                  <Label htmlFor="featured" className="cursor-pointer flex items-center gap-2">
                    {formData.featured ? <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" /> : <StarOff className="w-5 h-5 text-gray-400" />}
                    Mark as Featured Resource
                  </Label>
                </div>
              </>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={isSaving} className="flex-1 bg-blue-600 hover:bg-blue-700">
              {isSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />{editingResource ? 'Update Resource' : 'Create Resource'}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResourceForm;
